import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowUp,
  MessageCircle,
  Truck,
  IndianRupee,
  FileText,
  RotateCcw,
  Smartphone,
  CreditCard,
  Banknote,
  Landmark,
  Wallet,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { ThemePicker, ThemeToggle } from '@kss/ui';

/**
 * Storefront footer for Kristy Mart. Bundles three regions from the
 * v2 master prompt because they all live above the page bottom and
 * share rhythm / theme variables:
 *
 *   - `<TrustStrip />`     (Fix 10) — 4 value-prop columns
 *   - `<NewsletterSection />` (Fix 11) — email + WhatsApp signup
 *   - `<Footer />`         (Fix 5)  — Shop / Help / Company / Account
 *
 * Theme tokens (`--color-*`) and the existing palette + light/dark
 * toggle are preserved exactly; only copy + structure changed.
 */

const SHOP_LINKS = [
  { to: '/shop', label: 'All Products' },
  { to: '/shop?sale=true', label: 'Deals' },
  { to: '/shop?sort=new', label: 'New Arrivals' },
  { to: '/shop?sort=pop', label: 'Best Sellers' },
  { to: '/shop', label: 'Brands' },
];

const HELP_LINKS = [
  { to: '/account/track-order', label: 'Track Order' },
  { to: '/account/orders', label: 'Returns & Refunds' },
  { to: '/contact', label: 'Shipping Info' },
  { to: '/contact', label: 'FAQs' },
  { to: '/contact', label: 'Contact Us' },
];

const COMPANY_LINKS = [
  { to: '/about', label: 'About Kristy Mart' },
  { to: '/about', label: 'Careers' },
  { to: '/about', label: 'Blog' },
  { to: '/about', label: 'Press' },
];

const ACCOUNT_LINKS = [
  { to: '/login', label: 'Log In' },
  { to: '/login?mode=signup', label: 'Sign Up' },
  { to: '/account/orders', label: 'My Orders' },
  { to: '/account/wishlist', label: 'Wishlist' },
  { to: '/account/addresses', label: 'Saved Addresses' },
];

/* Payment-method badges shown in the "We accept" row. Each entry
   pairs a lucide icon with the brand label so the row reads as
   icons-with-text — no new icon assets are introduced. Brand-specific
   colors stay token-driven (the icon inherits text-secondary) so dark
   mode keeps working. */
const PAYMENT_BADGES: ReadonlyArray<{ label: string; Icon: LucideIcon }> = [
  { label: 'UPI',        Icon: Smartphone },
  { label: 'Visa',       Icon: CreditCard },
  { label: 'Mastercard', Icon: CreditCard },
  { label: 'RuPay',      Icon: CreditCard },
  { label: 'Net Banking',Icon: Landmark },
  { label: 'Wallet',     Icon: Wallet },
  { label: 'COD / Cash', Icon: Banknote },
];

function TrustStrip() {
  return (
    <div className="border-y border-[var(--color-border)] bg-[var(--color-bg-card)]">
      <div className="mx-auto grid max-w-7xl grid-cols-2 gap-4 px-4 py-5 sm:px-6 sm:py-6 md:grid-cols-4">
        <TrustItem
          icon={<Truck className="h-5 w-5" />}
          title="Free Delivery"
          subtitle="on orders above ₹499"
        />
        <TrustItem
          icon={<IndianRupee className="h-5 w-5" />}
          title="Cash on Delivery"
          subtitle="pan-India"
        />
        <TrustItem
          icon={<FileText className="h-5 w-5" />}
          title="GST Invoice"
          subtitle="for business buyers"
        />
        <TrustItem
          icon={<RotateCcw className="h-5 w-5" />}
          title="Easy 30-day Returns"
          subtitle="no questions asked"
        />
      </div>
    </div>
  );
}

function TrustItem({
  icon,
  title,
  subtitle,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
}) {
  return (
    <div className="flex items-center gap-3">
      <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--color-primary-light)] text-[var(--color-primary)]">
        {icon}
      </span>
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold text-[var(--color-text-primary)]">
          {title}
        </div>
        <div className="truncate text-xs text-[var(--color-text-secondary)]">
          {subtitle}
        </div>
      </div>
    </div>
  );
}

function NewsletterSection() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      // Stub endpoint per spec. The decorateError interceptor will
      // surface real backend errors; soft-fail to a friendly toast
      // so newsletter capture never blocks the page.
      await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      }).catch(() => undefined);
      setDone('Thanks — we\'ll email you the next deal.');
      setEmail('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="border-b border-[var(--color-border)] bg-[var(--color-bg-topbar)]">
      <div className="mx-auto flex max-w-7xl flex-col items-start gap-4 px-4 py-6 sm:px-6 md:flex-row md:items-center md:justify-between md:py-8">
        <div className="min-w-0">
          <div className="text-base font-semibold text-[var(--color-text-primary)] sm:text-lg">
            Get deals first.
          </div>
          <p className="mt-0.5 text-xs text-[var(--color-text-secondary)] sm:text-sm">
            Subscribe for exclusive offers and new arrivals.
          </p>
        </div>
        <form
          onSubmit={handleSubmit}
          className="flex w-full max-w-md flex-col gap-2 sm:flex-row"
        >
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            aria-label="Email address"
            required
            className="h-10 flex-1 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] px-3 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-primary)] focus:outline-none"
          />
          <button
            type="submit"
            disabled={!valid || submitting}
            className="inline-flex h-10 items-center justify-center rounded-md bg-[var(--color-primary)] px-4 text-sm font-semibold text-[var(--color-primary-fg)] hover:bg-[var(--color-primary-hover)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? 'Subscribing…' : 'Subscribe'}
          </button>
        </form>
      </div>
      <div className="mx-auto max-w-7xl px-4 pb-5 sm:px-6">
        <a
          href="https://wa.me/919999999999"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-[var(--color-primary)] hover:underline sm:text-sm"
        >
          <MessageCircle className="h-3.5 w-3.5" />
          Or get updates on WhatsApp →
        </a>
        {done && (
          <span className="ml-3 text-xs text-[var(--color-success)] sm:text-sm">
            {done}
          </span>
        )}
      </div>
    </div>
  );
}

export function WebFooter() {
  const scrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <>
      <TrustStrip />
      <NewsletterSection />
      <footer className="border-t border-[var(--color-border)] bg-[var(--color-bg-topbar)]">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-12">
          <div className="mb-6 flex flex-wrap items-start justify-between gap-4 sm:mb-8">
            <div>
              <div className="flex items-center gap-2 text-lg font-bold text-[var(--color-text-primary)]">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-[var(--color-primary)] text-white">
                  K
                </span>
                <span className="flex flex-col leading-tight">
                  <span>KSS</span>
                  <span className="text-[10px] font-medium text-[var(--color-text-muted)]">
                    Kristy Mart
                  </span>
                </span>
              </div>
              <p className="mt-3 max-w-sm text-sm text-[var(--color-text-secondary)]">
                India&apos;s marketplace for everyday essentials.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <ThemePicker />
              <ThemeToggle />
              <button
                type="button"
                aria-label="Scroll to top"
                onClick={scrollTop}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] hover:bg-[var(--color-bg-card-hover)]"
              >
                <ArrowUp className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Four columns: Shop / Help / Company / Account (Fix 5).
              Each column groups storefront-facing links — no admin
              ("Demo", "Components", "Panel") leaks here. */}
          <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
            <FooterColumn title="Shop" links={SHOP_LINKS} />
            <FooterColumn title="Help" links={HELP_LINKS} />
            <FooterColumn title="Company" links={COMPANY_LINKS} />
            <FooterColumn title="Account" links={ACCOUNT_LINKS} />
          </div>

          {/* Payment-method row — icon + label badges so the row
              reads visually like a row of payment-method tiles. The
              icons come from lucide-react (already in repo) so no new
              assets are introduced, and the colour scheme stays
              theme-token-driven (badge background = card, text =
              secondary) so dark mode keeps working. */}
          <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-[var(--color-border)] pt-6">
            <span className="mr-2 text-xs font-medium uppercase tracking-wider text-[var(--color-text-muted)]">
              We accept
            </span>
            {PAYMENT_BADGES.map(({ label, Icon }) => (
              <span
                key={label}
                title={label}
                className="inline-flex items-center gap-1.5 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] px-2 py-1 text-[11px] font-semibold text-[var(--color-text-secondary)]"
              >
                <Icon className="h-3.5 w-3.5 text-[var(--color-primary)]" />
                {label}
              </span>
            ))}
          </div>
        </div>

        <div className="border-t border-[var(--color-border)] py-4 text-center text-xs text-[var(--color-text-muted)]">
          © {new Date().getFullYear()} Kristy Mart, a KSS product.
          {' · '}
          <Link to="/about" className="hover:underline">
            Privacy
          </Link>
          {' · '}
          <Link to="/about" className="hover:underline">
            Terms
          </Link>
          {' · '}
          <Link to="/about" className="hover:underline">
            GST Info
          </Link>
        </div>
      </footer>
    </>
  );
}

function FooterColumn({
  title,
  links,
}: {
  title: string;
  links: ReadonlyArray<{ to: string; label: string }>;
}) {
  return (
    <div className="min-w-0">
      <div className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
        {title}
      </div>
      <ul className="space-y-2 text-sm text-[var(--color-text-secondary)]">
        {links.map((l) => (
          <li key={l.label}>
            <Link to={l.to} className="hover:text-[var(--color-text-primary)]">
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default WebFooter;
